import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    App,
    Button,
    Collapse,
    DatePicker,
    Empty,
    Form,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Select,
    Space,
    Spin,
    Switch,
    Table,
    Tabs,
    Tag,
    Tooltip,
    Typography,
} from "antd";
import {
    CloudUploadOutlined,
    DeleteOutlined,
    DownloadOutlined,
    InfoCircleOutlined,
    KeyOutlined,
    PlusOutlined,
    ReloadOutlined,
    SafetyCertificateOutlined,
    SettingOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

import externalCrawlAdminApi from "../../../api/externalCrawlAdminApi";

const { Text } = Typography;

const MAX_AUTO_RANGE_DAYS = 60;
const MAX_AUTO_RANGE_MONTHS = 2;
const MAX_FILE_FILTER_RANGE_DAYS = 60;
// 与后端 ADMIN_BATCH_DOWNLOAD_MAX_FILES 保持一致
const BATCH_DOWNLOAD_MAX_FILES = 100;
const ATLANTA_DAILY_RUN_TIME = "23:59";

const sitePresets = [
    {
        site_key: "myflydocs",
        site_name: "MyFlyDocs",
        display_name: "MyFlyDocs 文件抓取",
        hostPattern: /(^|\.)myflydocs\.com$/i,
        login_url: "https://www.myflydocs.com/clients",
        collector_key: "myflydocs-mcc-documents",
        auto_login_enabled: true,
    },
    {
        site_key: "atlanta",
        site_name: "Atlanta",
        display_name: "Atlanta Submitted Reports",
        hostPattern: /^live\.atlanta\.is$/i,
        login_url: "https://live.atlanta.is/Account/Login",
        collector_key: "atlanta-submitted-reports",
        auto_login_enabled: false,
    },
];

const twoColumnFormGridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
};

const compactFormItemStyle = {
    minWidth: 0,
};

const fullWidthControlStyle = {
    width: "100%",
};

const configModalFormStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 22,
};

const configSectionStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 12,
};

const configSectionHeaderStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 4,
};

const configSectionTitleStyle = {
    fontSize: 15,
    fontWeight: 600,
    color: "#1f1f1f",
};

const configSectionDescriptionStyle = {
    fontSize: 12,
    color: "#8c8c8c",
};

const settingListStyle = {
    border: "1px solid #f0f0f0",
    borderRadius: 6,
    overflow: "hidden",
};

const settingRowStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 20,
    padding: "14px 16px",
    borderBottom: "1px solid #f0f0f0",
};

const settingRowLastStyle = {
    ...settingRowStyle,
    borderBottom: 0,
};

const settingTextStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    minWidth: 0,
};

const settingTitleStyle = {
    fontSize: 14,
    fontWeight: 500,
    color: "#262626",
};

const settingDescriptionStyle = {
    fontSize: 12,
    color: "#8c8c8c",
};

const advancedCollapseStyle = {
    marginTop: -4,
};

const formLabelWithTooltipStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
};

const formLabelTooltipIconStyle = {
    color: "#8c8c8c",
    fontSize: 13,
};

const statusColor = {
    pending: "default",
    running: "processing",
    completed: "success",
    failed: "error",
    cancelled: "warning",
    uploaded: "success",
};

// T17: 抓取页文件 Tab "识别状态" 标签映射。
// 取值来自 _serialize_file 输出的 flight_log_recognition_status / flight_log_recognition_is_deleted
// （见 backend/services/external_crawl/service.py + ExternalCrawlFileResponse schema）。
//
// 显示规则（与 spec T17 对齐）:
//   pending / recognizing       -> 蓝「待识别」
//   parsed / review_required    -> 橙「已识别 待复核」
//   confirmed                   -> 绿「已确认」
//   failed                      -> 红「识别失败」
//   rejected / 对端 is_deleted  -> 灰「非 flight log」
//   字段为空（id 为 NULL）       -> 不显示标签（开关关闭 / 站点不在白名单 / 通知失败）
const RECOGNITION_STATUS_TAG = {
    pending: { label: "待识别", color: "blue" },
    recognizing: { label: "待识别", color: "blue" },
    parsed: { label: "已识别 待复核", color: "orange" },
    review_required: { label: "已识别 待复核", color: "orange" },
    confirmed: { label: "已确认", color: "green" },
    failed: { label: "识别失败", color: "red" },
    rejected: { label: "非 flight log", color: "default" },
};

const renderRecognitionStatusTag = (record) => {
    if (!record?.flight_log_recognition_id) {
        // 还没接入识别（开关关闭 / 通知失败 / 站点不在白名单）—— 不显示标签
        return null;
    }
    if (record.flight_log_recognition_is_deleted) {
        // 对端被软删 / 物删（悬挂引用）—— 等价于"非 flight log"
        return <Tag color="default" style={{ margin: 0 }}>非 flight log</Tag>;
    }
    const meta = RECOGNITION_STATUS_TAG[record.flight_log_recognition_status];
    if (!meta) {
        // 未知 status —— 退化为不带颜色的原文，避免静默吞掉异常
        return <Tag style={{ margin: 0 }}>{record.flight_log_recognition_status || "—"}</Tag>;
    }
    // 标签可点击：跳到识别详情（detail_id 用 fuel-reports 现有路由参数）
    return (
        <a
            href={`/settlement/fuel-reports?tab=flight-log&detail_id=${record.flight_log_recognition_id}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "none" }}
        >
            <Tag color={meta.color} style={{ margin: 0, cursor: "pointer" }}>
                {meta.label}
            </Tag>
        </a>
    );
};

// 外部抓取自动接入加油单识别（Atlanta）：文件 Tab "识别状态" 标签映射。
// 取值来自 _serialize_file 输出的 fuel_form_image_status / fuel_form_image_is_deleted。
//
// 显示规则（与 spec S5.1 对齐）:
//   pending_extract / pending_match  -> 蓝「待识别」
//   pending_review* 三种             -> 橙「待审核」
//   confirmed                        -> 绿「已确认」
//   rejected                         -> 灰「已拒绝」
//   failed_permanent                 -> 红「识别失败」
//   字段为空（id 为 NULL）            -> 不显示标签（开关关闭 / 站点不在白名单 / 通知失败）
const FUEL_FORM_STATUS_TAG = {
    pending_extract: { label: "待识别", color: "blue" },
    pending_match: { label: "待识别", color: "blue" },
    pending_review: { label: "待审核", color: "orange" },
    pending_review_low_confidence: { label: "待审核", color: "orange" },
    pending_review_ambiguous_match: { label: "待审核", color: "orange" },
    confirmed: { label: "已确认", color: "green" },
    rejected: { label: "已拒绝", color: "default" },
    failed_permanent: { label: "识别失败", color: "red" },
};

const renderFuelFormStatusTag = (record) => {
    if (!record?.fuel_form_image_id) {
        return null;
    }
    if (record.fuel_form_image_is_deleted) {
        // 对端被软删 / 物删（悬挂引用）
        return <Tag color="default" style={{ margin: 0 }}>识别记录已删除</Tag>;
    }
    const meta = FUEL_FORM_STATUS_TAG[record.fuel_form_image_status];
    if (!meta) {
        // 未知 status —— 退化为不带颜色的原文，避免静默吞掉异常
        return <Tag style={{ margin: 0 }}>{record.fuel_form_image_status || "—"}</Tag>;
    }
    // 标签可点击：跳到加油单识别详情（S5.2）
    return (
        <a
            href={`/settlement/fuel-reports?tab=image&detail_id=${record.fuel_form_image_id}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "none" }}
        >
            <Tag color={meta.color} style={{ margin: 0, cursor: "pointer" }}>
                {meta.label}
            </Tag>
        </a>
    );
};

const formatDateTime = (value) => {
    if (!value) {
        return "—";
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm:ss") : value;
};

const formatDate = (value) => {
    if (!value) {
        return "—";
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : value;
};

const formatFileSize = (value) => {
    if (!value) {
        return "—";
    }
    if (value >= 1024 * 1024) {
        return `${(value / 1024 / 1024).toFixed(1)} MB`;
    }
    return `${Math.round(value / 1024)} KB`;
};

const isImageMimeType = (value) => String(value || "").toLowerCase().startsWith("image/");

const getFileExtension = (record) => {
    const name = record?.file_name || record?.external_file_uid || record?.oss_key || "";
    const normalized = String(name).split("?")[0].split("#")[0].toLowerCase();
    const index = normalized.lastIndexOf(".");
    return index >= 0 ? normalized.slice(index + 1) : "";
};

const isImageFile = (mimeType, record) => (
    isImageMimeType(mimeType)
    || ["jpg", "jpeg", "png", "webp", "gif", "bmp", "tif", "tiff"].includes(getFileExtension(record))
);

const buildFileRequestParams = (configId, dateRange) => {
    const params = { url_config_id: configId, limit: 300 };
    if (Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
        params.date_from = dateRange[0].format("YYYY-MM-DD");
        params.date_to = dateRange[1].format("YYYY-MM-DD");
    }
    return params;
};

const isFileDateRangeTooLong = (dateRange) => (
    Array.isArray(dateRange)
    && dateRange[0]
    && dateRange[1]
    && dateRange[1].diff(dateRange[0], "day") > MAX_FILE_FILTER_RANGE_DAYS
);

const inferSitePreset = (entryUrl) => {
    let parsed;
    try {
        parsed = new URL(String(entryUrl || "").trim());
    } catch (error) {
        throw new Error("请输入有效的入口 URL");
    }
    const host = parsed.hostname.toLowerCase();
    const preset = sitePresets.find((item) => item.hostPattern.test(host));
    if (!preset) {
        throw new Error("当前仅支持 myflydocs.com 和 live.atlanta.is 的抓取配置");
    }
    return { preset, host };
};

const labelWithTooltip = (label, title) => (
    <span style={formLabelWithTooltipStyle}>
        <span>{label}</span>
        <Tooltip title={title}>
            <InfoCircleOutlined style={formLabelTooltipIconStyle} />
        </Tooltip>
    </span>
);

const compactStats = (stats) => {
    if (!stats || typeof stats !== "object") {
        return "—";
    }
    const keys = ["uploaded", "skipped", "failed", "unavailable", "dates_scanned", "dates_total"];
    return keys
        .filter((key) => stats[key] !== undefined && stats[key] !== null)
        .map((key) => `${key}: ${stats[key]}`)
        .join(" / ") || "—";
};

const getSiteKeyFromConfigLike = (record) => record?.site_key || inferSitePreset(record?.entry_url).preset.site_key;

const buildDefaultRuleValues = (siteKey, record = null) => {
    if (siteKey === "atlanta") {
        return {
            schedule_type: "daily",
            daily_run_time: record?.daily_run_time || ATLANTA_DAILY_RUN_TIME,
            range_type: "rolling",
            rolling_unit: "day",
            rolling_value: 1,
            rolling_days: 1,
            interval_minutes: null,
            force_rescan_recent_days: record?.force_rescan_recent_days ?? 0,
        };
    }
    if (siteKey === "myflydocs") {
        const monthlyValue = Math.min(
            MAX_AUTO_RANGE_MONTHS,
            Math.max(1, Number(record?.rolling_value || ((record?.rolling_days || 0) > 30 ? 2 : 1))),
        );
        return {
            schedule_type: "interval",
            daily_run_time: null,
            range_type: "rolling",
            rolling_unit: "month",
            rolling_value: monthlyValue,
            rolling_days: null,
            interval_minutes: record?.interval_minutes || 60,
            force_rescan_recent_days: record?.force_rescan_recent_days ?? 0,
        };
    }
    return {
        schedule_type: record?.schedule_type || "interval",
        daily_run_time: record?.daily_run_time || null,
        range_type: record?.range_type || "rolling",
        rolling_unit: record?.rolling_unit || "day",
        rolling_value: record?.rolling_value || null,
        rolling_days: record?.rolling_days || 1,
        interval_minutes: record?.interval_minutes || 60,
        force_rescan_recent_days: record?.force_rescan_recent_days ?? 0,
    };
};

const formatAutoRuleText = (record) => {
    if (record?.range_type === "fixed") {
        return `${formatDate(record.fixed_start_date)} 至 ${formatDate(record.fixed_end_date)}`;
    }
    if (record?.site_key === "atlanta" || record?.schedule_type === "daily") {
        return `每日 ${record.daily_run_time || ATLANTA_DAILY_RUN_TIME}；Today 快捷项抓当天 00:00 至 23:59`;
    }
    if (record?.rolling_unit === "month") {
        return `近 ${record.rolling_value || 1} 个月`;
    }
    return `近 ${record?.rolling_value || record?.rolling_days || "-"} 天`;
};

const normalizeConfigValues = (values, existingConfig = null) => {
    const { preset, host } = inferSitePreset(values.entry_url);
    const defaults = buildDefaultRuleValues(preset.site_key, existingConfig);
    const rangeType = existingConfig?.range_type === "fixed" ? "fixed" : defaults.range_type;
    const isFixedRange = rangeType === "fixed";
    return {
        organization_id: values.organization_id ?? null,
        site_key: preset.site_key,
        site_name: preset.site_name,
        display_name: values.display_name || preset.display_name,
        entry_url: String(values.entry_url || "").trim(),
        login_url: values.login_url || preset.login_url || null,
        auto_login_enabled: preset.site_key === "atlanta" ? false : Boolean(values.auto_login_enabled),
        allowed_domain: host,
        collector_key: preset.collector_key,
        collector_version: existingConfig?.collector_version || null,
        oss_prefix: existingConfig?.oss_prefix || "external-crawl",
        auto_enabled: Boolean(values.auto_enabled),
        schedule_type: defaults.schedule_type,
        daily_run_time: defaults.schedule_type === "daily" ? (values.daily_run_time || defaults.daily_run_time) : null,
        range_type: rangeType,
        rolling_unit: isFixedRange ? (existingConfig?.rolling_unit || defaults.rolling_unit) : defaults.rolling_unit,
        rolling_value: isFixedRange ? (existingConfig?.rolling_value ?? null) : (values.rolling_value ?? defaults.rolling_value),
        rolling_days: isFixedRange ? (existingConfig?.rolling_days ?? null) : defaults.rolling_days,
        fixed_start_date: isFixedRange ? (existingConfig?.fixed_start_date ?? null) : null,
        fixed_end_date: isFixedRange ? (existingConfig?.fixed_end_date ?? null) : null,
        force_rescan_recent_days: values.force_rescan_recent_days ?? defaults.force_rescan_recent_days,
        interval_minutes: defaults.schedule_type === "interval" ? (values.interval_minutes ?? defaults.interval_minutes) : null,
        enabled: values.enabled ?? true,
        remark: values.remark || null,
    };
};

const toConfigFormValues = (record) => {
    const siteKey = record ? getSiteKeyFromConfigLike(record) : "";
    const defaults = buildDefaultRuleValues(siteKey, record);
    return {
        organization_id: record?.organization_id ?? null,
        display_name: record?.display_name || "",
        entry_url: record?.entry_url || "",
        login_url: record?.login_url || "",
        site_username: "",
        site_password: "",
        auto_login_enabled: record ? Boolean(record.auto_login_enabled) : false,
        auto_enabled: Boolean(record?.auto_enabled),
        schedule_type: defaults.schedule_type,
        daily_run_time: defaults.daily_run_time,
        rolling_unit: defaults.rolling_unit,
        rolling_value: defaults.rolling_value,
        rolling_days: defaults.rolling_days,
        force_rescan_recent_days: defaults.force_rescan_recent_days,
        interval_minutes: defaults.interval_minutes,
        enabled: record?.enabled ?? true,
        remark: record?.remark || "",
    };
};

const ExternalCrawlConfigPage = () => {
    const { message } = App.useApp();
    const [configs, setConfigs] = useState([]);
    const [selectedConfigId, setSelectedConfigId] = useState(null);
    const [credentials, setCredentials] = useState([]);
    const [permissions, setPermissions] = useState([]);
    const [permissionDraft, setPermissionDraft] = useState([]);
    const [userOptions, setUserOptions] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [files, setFiles] = useState([]);
    const [selectedFileIds, setSelectedFileIds] = useState([]);
    const [batchDownloading, setBatchDownloading] = useState(false);
    const [fileDateRange, setFileDateRange] = useState(null);
    const [snapshots, setSnapshots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [fileLoading, setFileLoading] = useState(false);
    const [configModalOpen, setConfigModalOpen] = useState(false);
    const [credentialModalOpen, setCredentialModalOpen] = useState(false);
    const [filePreviewOpen, setFilePreviewOpen] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [editingConfig, setEditingConfig] = useState(null);
    const [editingCredential, setEditingCredential] = useState(null);
    const [configForm] = Form.useForm();
    const [credentialForm] = Form.useForm();

    const selectedConfig = useMemo(
        () => configs.find((item) => item.id === selectedConfigId) || null,
        [configs, selectedConfigId],
    );
    const watchedEntryUrl = Form.useWatch("entry_url", configForm);
    const watchedAutoLoginEnabled = Form.useWatch("auto_login_enabled", configForm);
    const watchedAutoEnabled = Form.useWatch("auto_enabled", configForm);
    const watchedSitePreset = useMemo(() => {
        if (!watchedEntryUrl) {
            return null;
        }
        try {
            return inferSitePreset(watchedEntryUrl).preset;
        } catch (error) {
            return null;
        }
    }, [watchedEntryUrl]);
    const watchedSiteKey = watchedSitePreset?.site_key || editingConfig?.site_key || "";
    const autoLoginSwitchDisabled = watchedSitePreset?.site_key === "atlanta";
    const showInitialCredentialFields = !editingConfig && Boolean(watchedAutoLoginEnabled);

    const loadConfigs = useCallback(async () => {
        setLoading(true);
        try {
            const response = await externalCrawlAdminApi.getConfigs();
            const items = response.data?.items || [];
            setConfigs(items);
            setSelectedConfigId((current) => (current && items.some((item) => item.id === current) ? current : items[0]?.id || null));
            return items;
        } catch (error) {
            message.error(error.response?.data?.detail || "加载抓取配置失败");
            return [];
        } finally {
            setLoading(false);
        }
    }, [message]);

    const loadUserOptions = useCallback(async () => {
        try {
            const response = await externalCrawlAdminApi.getUserOptions({ limit: 200 });
            setUserOptions(response.data?.items || []);
        } catch (error) {
            message.error(error.response?.data?.detail || "加载用户列表失败");
        }
    }, [message]);

    const loadFiles = useCallback(
        async (configId, dateRange = null) => {
            if (!configId) {
                setFiles([]);
                setSelectedFileIds([]);
                return;
            }
            if (isFileDateRangeTooLong(dateRange)) {
                message.warning(`业务日期范围最多支持 ${MAX_FILE_FILTER_RANGE_DAYS} 天`);
                return;
            }
            setFileLoading(true);
            try {
                const response = await externalCrawlAdminApi.getFiles(buildFileRequestParams(configId, dateRange));
                setFiles(response.data?.items || []);
                setSelectedFileIds([]);
            } catch (error) {
                message.error(error.response?.data?.detail || "加载文件列表失败");
            } finally {
                setFileLoading(false);
            }
        },
        [message],
    );

    const loadConfigDetails = useCallback(
        async (configId, fileRange = null) => {
            if (!configId) {
                setCredentials([]);
                setPermissions([]);
                setPermissionDraft([]);
                setTasks([]);
                setFiles([]);
                setSelectedFileIds([]);
                setSnapshots([]);
                return;
            }
            setDetailLoading(true);
            try {
                const [credentialsResponse, permissionsResponse, tasksResponse, filesResponse, snapshotsResponse] = await Promise.all([
                    externalCrawlAdminApi.getCredentials(configId),
                    externalCrawlAdminApi.getPermissions(configId),
                    externalCrawlAdminApi.getTasks({ url_config_id: configId, limit: 50 }),
                    externalCrawlAdminApi.getFiles(buildFileRequestParams(configId, fileRange)),
                    externalCrawlAdminApi.getDateSnapshots({ url_config_id: configId, limit: 100 }),
                ]);
                const permissionItems = permissionsResponse.data?.items || [];
                setCredentials(credentialsResponse.data?.items || []);
                setPermissions(permissionItems);
                setPermissionDraft(permissionItems.map((item) => ({ ...item })));
                setTasks(tasksResponse.data?.items || []);
                setFiles(filesResponse.data?.items || []);
                setSelectedFileIds([]);
                setSnapshots(snapshotsResponse.data?.items || []);
            } catch (error) {
                message.error(error.response?.data?.detail || "加载配置详情失败");
            } finally {
                setDetailLoading(false);
            }
        },
        [message],
    );

    useEffect(() => {
        loadConfigs();
        loadUserOptions();
    }, [loadConfigs, loadUserOptions]);

    useEffect(() => {
        setFileDateRange(null);
        loadConfigDetails(selectedConfigId, null);
    }, [loadConfigDetails, selectedConfigId]);

    useEffect(() => {
        if (autoLoginSwitchDisabled && configForm.getFieldValue("auto_login_enabled")) {
            configForm.setFieldsValue({ auto_login_enabled: false });
        }
    }, [autoLoginSwitchDisabled, configForm]);

    const openCreateConfig = () => {
        setEditingConfig(null);
        configForm.setFieldsValue(toConfigFormValues(null));
        setConfigModalOpen(true);
    };

    const openEditConfig = (record) => {
        setEditingConfig(record);
        configForm.setFieldsValue(toConfigFormValues(record));
        setConfigModalOpen(true);
    };

    const applyEntryUrlPreset = () => {
        const entryUrl = configForm.getFieldValue("entry_url");
        if (!entryUrl) {
            return;
        }
        try {
            const { preset } = inferSitePreset(entryUrl);
            const currentName = configForm.getFieldValue("display_name");
            const defaultRules = buildDefaultRuleValues(preset.site_key);
            const nextValues = {
                display_name: currentName || preset.display_name,
                login_url: configForm.getFieldValue("login_url") || preset.login_url,
                schedule_type: defaultRules.schedule_type,
                daily_run_time: defaultRules.daily_run_time,
                rolling_unit: defaultRules.rolling_unit,
                rolling_value: defaultRules.rolling_value,
                rolling_days: defaultRules.rolling_days,
                interval_minutes: defaultRules.interval_minutes,
                force_rescan_recent_days: defaultRules.force_rescan_recent_days,
            };
            if (!editingConfig && !configForm.isFieldTouched("auto_login_enabled")) {
                nextValues.auto_login_enabled = preset.auto_login_enabled;
            }
            configForm.setFieldsValue(nextValues);
        } catch (error) {
            message.warning(error.message || "无法识别入口 URL");
        }
    };

    const buildCredentialPayload = (values) => {
        if (editingConfig) {
            return null;
        }
        if (!values.auto_login_enabled) {
            return null;
        }
        const username = String(values.site_username || "").trim();
        const password = String(values.site_password || "");
        if (!username || !password) {
            throw new Error("开启自动登录时需要填写网站账号和密码");
        }
        return { username, password, enabled: true };
    };

    const submitConfig = async () => {
        let rawValues;
        try {
            rawValues = await configForm.validateFields();
        } catch (error) {
            if (error.errorFields) {
                return;
            }
            message.error(error.message || "请检查抓取配置表单");
            return;
        }

        let values;
        let credentialPayload;
        try {
            values = normalizeConfigValues(rawValues, editingConfig);
            credentialPayload = buildCredentialPayload(rawValues);
        } catch (error) {
            message.error(error.message || "请检查抓取配置表单");
            return;
        }

        let savedConfigId = editingConfig?.id || null;
        try {
            if (editingConfig) {
                await externalCrawlAdminApi.updateConfig(editingConfig.id, values);
            } else {
                const response = await externalCrawlAdminApi.createConfig(values);
                savedConfigId = response.data?.id;
            }
        } catch (error) {
            message.error(error.response?.data?.detail || "保存抓取配置失败");
            return;
        }

        let credentialSaved = false;
        if (credentialPayload && savedConfigId) {
            try {
                await externalCrawlAdminApi.createCredential(savedConfigId, credentialPayload);
                credentialSaved = true;
            } catch (error) {
                message.warning(error.response?.data?.detail || "抓取配置已保存，但网站账号密码保存失败，请在凭据 tab 中补充");
            }
        }

        if (!editingConfig && savedConfigId) {
            setSelectedConfigId(savedConfigId);
        }
        message.success(credentialPayload && credentialSaved ? "抓取配置和网站凭据已保存" : "抓取配置已保存");
        try {
            setConfigModalOpen(false);
            await Promise.all([loadConfigs(), loadConfigDetails(savedConfigId || selectedConfigId, fileDateRange)]);
        } catch (error) {
            message.error(error.response?.data?.detail || "刷新抓取配置失败");
        }
    };

    const toggleConfigEnabled = async (record, enabled) => {
        try {
            await externalCrawlAdminApi.setConfigEnabled(record.id, enabled);
            message.success(enabled ? "配置已启用" : "配置已停用");
            await loadConfigs();
        } catch (error) {
            message.error(error.response?.data?.detail || "更新配置状态失败");
        }
    };

    const deleteConfig = async (record) => {
        const remainingConfigs = configs.filter((item) => item.id !== record.id);
        const nextSelectedId = selectedConfigId === record.id ? (remainingConfigs[0]?.id || null) : selectedConfigId;
        const nextFileDateRange = selectedConfigId === record.id ? null : fileDateRange;
        try {
            await externalCrawlAdminApi.deleteConfig(record.id);
            message.success("配置已删除");
            setSelectedConfigId(nextSelectedId);
            if (selectedConfigId === record.id) {
                setFileDateRange(null);
            }
            await Promise.all([loadConfigs(), loadConfigDetails(nextSelectedId, nextFileDateRange)]);
        } catch (error) {
            message.error(error.response?.data?.detail || "删除配置失败");
        }
    };

    const openCreateCredential = () => {
        setEditingCredential(null);
        credentialForm.setFieldsValue({ username: "", password: "", enabled: true });
        setCredentialModalOpen(true);
    };

    const openEditCredential = (record) => {
        setEditingCredential(record);
        credentialForm.setFieldsValue({ username: record.username, password: "", enabled: record.enabled });
        setCredentialModalOpen(true);
    };

    const submitCredential = async () => {
        if (!selectedConfig) {
            return;
        }
        try {
            const values = await credentialForm.validateFields();
            if (editingCredential) {
                const payload = { ...values };
                if (!payload.password) {
                    delete payload.password;
                }
                await externalCrawlAdminApi.updateCredential(editingCredential.id, payload);
                message.success("凭据已更新");
            } else {
                await externalCrawlAdminApi.createCredential(selectedConfig.id, values);
                message.success("凭据已创建");
            }
            setCredentialModalOpen(false);
            await Promise.all([loadConfigs(), loadConfigDetails(selectedConfig.id, fileDateRange)]);
        } catch (error) {
            if (error.errorFields) {
                return;
            }
            message.error(error.response?.data?.detail || "保存凭据失败");
        }
    };

    const toggleCredentialEnabled = async (record, enabled) => {
        try {
            await externalCrawlAdminApi.setCredentialEnabled(record.id, enabled);
            message.success(enabled ? "凭据已启用" : "凭据已停用");
            await Promise.all([loadConfigs(), loadConfigDetails(selectedConfig.id, fileDateRange)]);
        } catch (error) {
            message.error(error.response?.data?.detail || "更新凭据状态失败");
        }
    };

    const deleteCredential = async (record) => {
        if (!selectedConfig) {
            return;
        }
        try {
            await externalCrawlAdminApi.deleteCredential(record.id);
            message.success("凭据已删除");
            await Promise.all([loadConfigs(), loadConfigDetails(selectedConfig.id, fileDateRange)]);
        } catch (error) {
            message.error(error.response?.data?.detail || "删除凭据失败");
        }
    };

    const updatePermissionDraft = (userId, patch) => {
        setPermissionDraft((current) => current.map((item) => (item.user_id === userId ? { ...item, ...patch } : item)));
    };

    const addPermissionUser = (userId) => {
        const user = userOptions.find((item) => item.id === userId);
        if (!user || permissionDraft.some((item) => item.user_id === user.id)) {
            return;
        }
        setPermissionDraft((current) => [
            ...current,
            {
                id: `new-${user.id}`,
                user_id: user.id,
                username: user.username,
                full_name: user.full_name,
                email: user.email,
                can_manual: true,
                can_auto: false,
                manual_max_days: 60,
                enabled: true,
            },
        ]);
    };

    const removePermissionUser = (userId) => {
        setPermissionDraft((current) => current.filter((item) => item.user_id !== userId));
    };

    const validateRollingMonths = () => ({
        validator(_, value) {
            if (value === null || value === undefined) {
                return Promise.reject(new Error("请填写监控月数"));
            }
            if (value < 1 || value > MAX_AUTO_RANGE_MONTHS) {
                return Promise.reject(new Error(`监控月数必须在 1-${MAX_AUTO_RANGE_MONTHS} 个月之间`));
            }
            return Promise.resolve();
        },
    });

    const savePermissions = async () => {
        if (!selectedConfig) {
            return;
        }
        try {
            const items = permissionDraft.map((item) => ({
                user_id: item.user_id,
                organization_id: item.organization_id || null,
                can_manual: Boolean(item.can_manual),
                can_auto: Boolean(item.can_auto),
                manual_max_days: item.manual_max_days || 60,
                enabled: Boolean(item.enabled),
            }));
            const response = await externalCrawlAdminApi.replacePermissions(selectedConfig.id, items);
            const nextItems = response.data?.items || [];
            setPermissions(nextItems);
            setPermissionDraft(nextItems.map((item) => ({ ...item })));
            message.success("用户授权已保存");
            await loadConfigs();
        } catch (error) {
            message.error(error.response?.data?.detail || "保存用户授权失败");
        }
    };

    const openFilePreview = async (record) => {
        setPreviewFile(record);
        setPreviewData(null);
        setFilePreviewOpen(true);
        setPreviewLoading(true);
        try {
            const response = await externalCrawlAdminApi.getFilePreviewUrl(record.id, { expires_in_seconds: 600 });
            setPreviewData(response.data);
        } catch (error) {
            message.error(error.response?.data?.detail || "生成文件预览地址失败");
        } finally {
            setPreviewLoading(false);
        }
    };

    const closeFilePreview = () => {
        setFilePreviewOpen(false);
        setPreviewFile(null);
        setPreviewData(null);
    };

    /**
     * 优先用 file_name 作为下载名（hint），实际名以服务端响应头为准。
     */
    const triggerDownload = (downloadUrl, fileName) => {
        if (!downloadUrl) return;
        const link = document.createElement("a");
        link.href = downloadUrl;
        if (fileName) link.download = fileName;
        // 部分浏览器需要把 a 元素挂到 DOM 才能触发 click
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleBatchDownload = async () => {
        if (selectedFileIds.length === 0) {
            return;
        }
        if (selectedFileIds.length > BATCH_DOWNLOAD_MAX_FILES) {
            message.warning(`单次最多打包 ${BATCH_DOWNLOAD_MAX_FILES} 个文件，请分批下载`);
            return;
        }
        setBatchDownloading(true);
        try {
            const response = await externalCrawlAdminApi.batchDownloadFiles(selectedFileIds);
            const result = response.data || {};
            triggerDownload(result.download_url, result.file_name);
            if (result.skipped_count > 0) {
                message.warning(`已打包 ${result.count} 个文件，${result.skipped_count} 个文件拉取失败（详见压缩包内 _skipped.txt）`);
            } else {
                message.success(`已打包 ${result.count} 个文件，开始下载`);
            }
            setSelectedFileIds([]);
        } catch (error) {
            message.error(error.response?.data?.detail || "批量下载失败");
        } finally {
            setBatchDownloading(false);
        }
    };

    const applyFileDateFilter = () => {
        loadFiles(selectedConfigId, fileDateRange);
    };

    const resetFileDateFilter = () => {
        setFileDateRange(null);
        loadFiles(selectedConfigId, null);
    };

    const refreshFileList = () => {
        loadFiles(selectedConfigId, fileDateRange);
    };

    const configColumns = [
        {
            title: "站点",
            dataIndex: "display_name",
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{record.display_name}</Text>
                    <Text type="secondary">{record.site_name}</Text>
                </Space>
            ),
        },
        {
            title: "域名",
            dataIndex: "allowed_domain",
            ellipsis: true,
        },
        {
            title: "自动规则",
            width: 180,
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <Tag color={record.auto_enabled ? "processing" : "default"}>{record.auto_enabled ? "自动开启" : "手动"}</Tag>
                    <Text type="secondary">{formatAutoRuleText(record)}</Text>
                </Space>
            ),
        },
        {
            title: "凭据/授权",
            width: 150,
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <Text>{record.has_enabled_credential ? "凭据已配置" : "无启用凭据"}</Text>
                    <Text type="secondary">{record.auto_login_enabled ? "支持自动登录" : "手动登录"}</Text>
                    <Text type="secondary">{record.permission_count || 0} 个用户</Text>
                </Space>
            ),
        },
        {
            title: "状态",
            width: 100,
            render: (_, record) => <Switch size="small" checked={record.enabled} onChange={(checked) => toggleConfigEnabled(record, checked)} />,
        },
        {
            title: "操作",
            width: 150,
            render: (_, record) => (
                <Space onClick={(event) => event.stopPropagation()}>
                    <Button size="small" onClick={() => openEditConfig(record)}>
                        编辑
                    </Button>
                    <Popconfirm
                        title="删除抓取配置"
                        description="删除后该配置将从站点列表隐藏，历史任务和文件记录会保留。"
                        okText="删除"
                        cancelText="取消"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => deleteConfig(record)}
                    >
                        <Button size="small" danger icon={<DeleteOutlined />}>
                            删除
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const credentialColumns = [
        { title: "账号", dataIndex: "username" },
        { title: "版本", dataIndex: "credential_version", width: 90 },
        {
            title: "密码",
            dataIndex: "has_password",
            width: 100,
            render: (value) => (value ? "已配置" : "未配置"),
        },
        {
            title: "状态",
            width: 110,
            render: (_, record) => <Switch size="small" checked={record.enabled} onChange={(checked) => toggleCredentialEnabled(record, checked)} />,
        },
        { title: "更新时间", dataIndex: "updated_at", render: formatDateTime },
        {
            title: "操作",
            width: 150,
            render: (_, record) => (
                <Space size={8}>
                    <Button size="small" onClick={() => openEditCredential(record)}>
                        编辑
                    </Button>
                    <Popconfirm
                        title="删除登录凭据"
                        description="删除后该凭据将不可恢复；如果这是唯一启用凭据，插件将无法自动登录该站点。"
                        okText="删除"
                        cancelText="取消"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => deleteCredential(record)}
                    >
                        <Button size="small" danger icon={<DeleteOutlined />}>
                            删除
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const permissionColumns = [
        {
            title: "用户",
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{record.full_name || record.username}</Text>
                    <Text type="secondary">{record.email || record.username}</Text>
                </Space>
            ),
        },
        {
            title: "手动抓取",
            width: 110,
            render: (_, record) => (
                <Switch size="small" checked={record.can_manual} onChange={(checked) => updatePermissionDraft(record.user_id, { can_manual: checked })} />
            ),
        },
        {
            title: "自动抓取",
            width: 110,
            render: (_, record) => (
                <Switch size="small" checked={record.can_auto} onChange={(checked) => updatePermissionDraft(record.user_id, { can_auto: checked })} />
            ),
        },
        {
            title: "最大天数",
            width: 120,
            render: (_, record) => (
                <InputNumber
                    min={1}
                    max={60}
                    value={record.manual_max_days}
                    onChange={(value) => updatePermissionDraft(record.user_id, { manual_max_days: value || 1 })}
                />
            ),
        },
        {
            title: "启用",
            width: 90,
            render: (_, record) => (
                <Switch size="small" checked={record.enabled} onChange={(checked) => updatePermissionDraft(record.user_id, { enabled: checked })} />
            ),
        },
        {
            title: "操作",
            width: 90,
            render: (_, record) => (
                <Button size="small" danger onClick={() => removePermissionUser(record.user_id)}>
                    移除
                </Button>
            ),
        },
    ];

    const taskColumns = [
        { title: "ID", dataIndex: "id", width: 80 },
        { title: "类型", dataIndex: "task_type", width: 90 },
        {
            title: "状态",
            dataIndex: "status",
            width: 110,
            render: (value) => <Tag color={statusColor[value] || "default"}>{value}</Tag>,
        },
        { title: "日期范围", render: (_, record) => `${formatDate(record.range_start)} 至 ${formatDate(record.range_end)}` },
        { title: "统计", dataIndex: "stats_json", render: compactStats },
        { title: "更新时间", dataIndex: "updated_at", render: formatDateTime },
    ];

    const fileColumns = [
        { title: "ID", dataIndex: "id", width: 80 },
        { title: "日期", dataIndex: "date_key", width: 120, render: formatDate },
        {
            title: "文件名",
            dataIndex: "file_name",
            ellipsis: true,
            render: (value, record) => (
                <Button type="link" size="small" style={{ padding: 0, maxWidth: "100%" }} onClick={() => openFilePreview(record)}>
                    <Text ellipsis title={value || record.external_file_uid}>
                        {value || record.external_file_uid || "未命名文件"}
                    </Text>
                </Button>
            ),
        },
        {
            title: "状态",
            dataIndex: "status",
            width: 100,
            render: (value) => <Tag color={statusColor[value] || "default"}>{value}</Tag>,
        },
        {
            // T17: 识别状态标签。点击跳到识别详情。
            // myflydocs 行显示 Flight Log 识别状态（RECOGNITION_STATUS_TAG）；
            // atlanta 行显示加油单识别状态（FUEL_FORM_STATUS_TAG）。两者按站点互斥。
            title: "识别状态",
            key: "recognition_status",
            width: 130,
            render: (_, record) => renderRecognitionStatusTag(record) || renderFuelFormStatusTag(record),
        },
        { title: "大小", dataIndex: "file_size", width: 110, render: formatFileSize },
        { title: "上传时间", dataIndex: "uploaded_at", render: formatDateTime },
    ];

    const snapshotColumns = [
        { title: "日期", dataIndex: "date_key", width: 120, render: formatDate },
        { title: "声明文件", dataIndex: "document_count", width: 100 },
        { title: "已上传", dataIndex: "uploaded_file_count", width: 100 },
        { title: "原站状态", dataIndex: "work_status", width: 180 },
        { title: "扫描原因", dataIndex: "detail_scan_reason", width: 220 },
        { title: "最后列表发现", dataIndex: "last_seen_at", render: formatDateTime },
        { title: "最后详情扫描", dataIndex: "last_detail_scan_at", render: formatDateTime },
    ];

    const availableUserOptions = userOptions
        .filter((user) => !permissionDraft.some((permission) => permission.user_id === user.id))
        .map((user) => ({
            value: user.id,
            label: `${user.full_name || user.username} (${user.email || user.username})`,
        }));

    const previewMimeType = previewData?.mime_type || previewFile?.mime_type || "";
    const previewUrl = previewData?.preview_url || "";

    const renderPreviewContent = () => {
        if (previewLoading) {
            return (
                <div style={{ padding: 48, textAlign: "center" }}>
                    <Spin />
                </div>
            );
        }
        if (!previewUrl) {
            return <Empty description="暂无可预览文件" />;
        }
        if (isImageFile(previewMimeType, previewFile)) {
            return (
                <div style={{ textAlign: "center" }}>
                    <img
                        src={previewUrl}
                        alt={previewFile?.file_name || "preview"}
                        style={{
                            maxWidth: "100%",
                            maxHeight: "70vh",
                            objectFit: "contain",
                            display: "inline-block",
                        }}
                    />
                </div>
            );
        }
        return <Empty description="暂无可预览图片" />;
    };

    return (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 16 }}>
                <div>
                    <Typography.Title level={4} style={{ margin: 0 }}>
                        燃油账单抓取
                    </Typography.Title>
                    <Text type="secondary">配置 MyFlyDocs / Atlanta 的入口、凭据、授权和自动抓取规则。</Text>
                </div>
                <Space>
                    <Button icon={<ReloadOutlined />} onClick={() => Promise.all([loadConfigs(), loadConfigDetails(selectedConfigId, fileDateRange)])}>
                        刷新
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreateConfig}>
                        新建配置
                    </Button>
                </Space>
            </div>

            <Table
                size="middle"
                rowKey="id"
                loading={loading}
                dataSource={configs}
                columns={configColumns}
                pagination={false}
                rowClassName={(record) => (record.id === selectedConfigId ? "ant-table-row-selected" : "")}
                onRow={(record) => ({
                    onClick: () => setSelectedConfigId(record.id),
                })}
            />

            <div style={{ marginTop: 16 }}>
                {selectedConfig ? (
                    <Tabs
                        items={[
                            {
                                key: "files",
                                label: (
                                    <span>
                                        <CloudUploadOutlined /> 文件
                                    </span>
                                ),
                                children: (
                                    <div>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
                                            <Space wrap>
                                                <Text>业务日期</Text>
                                                <DatePicker.RangePicker
                                                    value={fileDateRange}
                                                    onChange={setFileDateRange}
                                                    format="YYYY-MM-DD"
                                                    allowClear
                                                    style={{ width: 260 }}
                                                />
                                                <Button type="primary" onClick={applyFileDateFilter} loading={fileLoading}>
                                                    查询
                                                </Button>
                                                <Button onClick={resetFileDateFilter} disabled={fileLoading}>
                                                    重置
                                                </Button>
                                            </Space>
                                            <Space>
                                                <Button
                                                    type="primary"
                                                    icon={<DownloadOutlined />}
                                                    onClick={handleBatchDownload}
                                                    loading={batchDownloading}
                                                    disabled={selectedFileIds.length === 0}
                                                >
                                                    下载选中{selectedFileIds.length > 0 ? ` (${selectedFileIds.length})` : ""}
                                                </Button>
                                                <Button icon={<ReloadOutlined />} onClick={refreshFileList} loading={fileLoading}>
                                                    刷新
                                                </Button>
                                            </Space>
                                        </div>
                                        <Table
                                            size="small"
                                            rowKey="id"
                                            loading={detailLoading || fileLoading}
                                            dataSource={files}
                                            columns={fileColumns}
                                            pagination={{ pageSize: 20 }}
                                            rowSelection={{
                                                selectedRowKeys: selectedFileIds,
                                                onChange: (keys) => setSelectedFileIds(keys),
                                                preserveSelectedRowKeys: true,
                                            }}
                                        />
                                        {selectedFileIds.length > 0 && (
                                            <div style={{ marginTop: 8 }}>
                                                <Space>
                                                    <Text type="secondary">已选 {selectedFileIds.length} 个文件（单次最多 {BATCH_DOWNLOAD_MAX_FILES} 个）</Text>
                                                    <Button type="link" size="small" onClick={() => setSelectedFileIds([])}>
                                                        取消选中
                                                    </Button>
                                                </Space>
                                            </div>
                                        )}
                                    </div>
                                ),
                            },
                            {
                                key: "credentials",
                                label: (
                                    <span>
                                        <KeyOutlined /> 凭据
                                    </span>
                                ),
                                children: (
                                    <div>
                                        <div style={{ marginBottom: 12 }}>
                                            <Space direction="vertical" size={0}>
                                                <Text strong>{selectedConfig.display_name}</Text>
                                                <Text type="secondary">用于后续密码轮换、账号变更和启停登录凭据。</Text>
                                            </Space>
                                        </div>
                                        {credentials.length === 0 ? (
                                            <Empty description="暂无登录凭据">
                                                <Button type="primary" icon={<PlusOutlined />} onClick={openCreateCredential}>
                                                    添加凭据
                                                </Button>
                                            </Empty>
                                        ) : (
                                            <Table
                                                size="small"
                                                rowKey="id"
                                                loading={detailLoading}
                                                dataSource={credentials}
                                                columns={credentialColumns}
                                                pagination={false}
                                            />
                                        )}
                                    </div>
                                ),
                            },
                            {
                                key: "permissions",
                                label: (
                                    <span>
                                        <SafetyCertificateOutlined /> 用户授权
                                    </span>
                                ),
                                children: (
                                    <div>
                                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                                            <Select
                                                showSearch
                                                placeholder="选择用户加入授权"
                                                style={{ width: 320 }}
                                                options={availableUserOptions}
                                                onSelect={addPermissionUser}
                                                value={null}
                                                filterOption={(input, option) => (option?.label || "").toLowerCase().includes(input.toLowerCase())}
                                            />
                                            <Space>
                                                <Button onClick={() => setPermissionDraft(permissions.map((item) => ({ ...item })))}>重置</Button>
                                                <Button type="primary" onClick={savePermissions}>
                                                    保存授权
                                                </Button>
                                            </Space>
                                        </div>
                                        <Table
                                            size="small"
                                            rowKey="user_id"
                                            loading={detailLoading}
                                            dataSource={permissionDraft}
                                            columns={permissionColumns}
                                            pagination={false}
                                        />
                                    </div>
                                ),
                            },
                            {
                                key: "tasks",
                                label: (
                                    <span>
                                        <SettingOutlined /> 任务
                                    </span>
                                ),
                                children: <Table size="small" rowKey="id" loading={detailLoading} dataSource={tasks} columns={taskColumns} pagination={false} />,
                            },
                            {
                                key: "snapshots",
                                label: "日期快照",
                                children: (
                                    <Table
                                        size="small"
                                        rowKey="id"
                                        loading={detailLoading}
                                        dataSource={snapshots}
                                        columns={snapshotColumns}
                                        pagination={{ pageSize: 20 }}
                                    />
                                ),
                            },
                        ]}
                    />
                ) : (
                    <Empty description="暂无抓取配置" />
                )}
            </div>

            <Modal
                open={filePreviewOpen}
                onCancel={closeFilePreview}
                width={920}
                destroyOnClose
                footer={null}
            >
                {renderPreviewContent()}
            </Modal>

            <Modal
                title={editingConfig ? "编辑抓取配置" : "新建抓取配置"}
                open={configModalOpen}
                onCancel={() => setConfigModalOpen(false)}
                onOk={submitConfig}
                width={720}
                destroyOnClose
            >
                <Form form={configForm} layout="vertical" style={configModalFormStyle}>
                    <section style={configSectionStyle}>
                        <div style={configSectionHeaderStyle}>
                            <span style={configSectionTitleStyle}>基础信息</span>
                            <span style={configSectionDescriptionStyle}>用于识别抓取站点和文件列表入口。</span>
                        </div>
                        <Form.Item name="display_name" label="配置名称" rules={[{ required: true, message: "请输入配置名称" }]}>
                            <Input placeholder="例如：MyFlyDocs MCC 文件抓取" />
                        </Form.Item>
                        <Form.Item name="entry_url" label="文件列表 URL" rules={[{ required: true, message: "请输入文件列表 URL" }]}>
                            <Input placeholder="https://www.myflydocs.com/clients/..." onBlur={applyEntryUrlPreset} />
                        </Form.Item>
                    </section>

                    <section style={configSectionStyle}>
                        <div style={configSectionHeaderStyle}>
                            <span style={configSectionTitleStyle}>登录方式</span>
                            <span style={configSectionDescriptionStyle}>控制插件在外部站点登录失效后的处理方式。</span>
                        </div>
                        <div style={settingListStyle}>
                            <div style={settingRowLastStyle}>
                                <div style={settingTextStyle}>
                                    <span style={settingTitleStyle}>支持自动登录</span>
                                    <span style={settingDescriptionStyle}>
                                        {autoLoginSwitchDisabled
                                            ? "Atlanta 当前走人工登录恢复，不自动提交账号密码。"
                                            : "关闭后插件会打开登录页，由用户人工登录。"}
                                    </span>
                                </div>
                                <Form.Item name="auto_login_enabled" valuePropName="checked" style={{ margin: 0 }}>
                                    <Switch disabled={autoLoginSwitchDisabled} />
                                </Form.Item>
                            </div>
                        </div>
                        {showInitialCredentialFields ? (
                            <div style={twoColumnFormGridStyle}>
                                <Form.Item
                                    name="site_username"
                                    label="网站账号"
                                    rules={[{ required: true, message: "请输入网站账号" }]}
                                    style={compactFormItemStyle}
                                >
                                    <Input autoComplete="off" />
                                </Form.Item>
                                <Form.Item
                                    name="site_password"
                                    label="网站密码"
                                    rules={[{ required: true, message: "请输入网站密码" }]}
                                    style={compactFormItemStyle}
                                    extra="保存后密码加密存储，不会在页面回显。"
                                >
                                    <Input.Password autoComplete="new-password" />
                                </Form.Item>
                            </div>
                        ) : null}
                        {editingConfig ? (
                            <Text type="secondary">
                                网站账号密码请在“凭据”Tab 中更换；编辑配置不会更新已保存的登录凭据。
                            </Text>
                        ) : null}
                    </section>

                    <section style={configSectionStyle}>
                        <div style={configSectionHeaderStyle}>
                            <span style={configSectionTitleStyle}>抓取规则</span>
                            <span style={configSectionDescriptionStyle}>设置配置是否启用，以及自动抓取的日期范围和执行频率。</span>
                        </div>
                        <div style={settingListStyle}>
                            <div style={settingRowStyle}>
                                <div style={settingTextStyle}>
                                    <span style={settingTitleStyle}>启用配置</span>
                                    <span style={settingDescriptionStyle}>关闭后插件和后台任务都不会使用该配置。</span>
                                </div>
                                <Form.Item name="enabled" valuePropName="checked" style={{ margin: 0 }}>
                                    <Switch />
                                </Form.Item>
                            </div>
                            <div style={settingRowLastStyle}>
                                <div style={settingTextStyle}>
                                    <span style={settingTitleStyle}>自动抓取</span>
                                    <span style={settingDescriptionStyle}>开启后按后台配置的频率自动监测新文件。</span>
                                </div>
                                <Form.Item name="auto_enabled" valuePropName="checked" style={{ margin: 0 }}>
                                    <Switch />
                                </Form.Item>
                            </div>
                        </div>
                        {watchedSiteKey === "myflydocs" ? (
                            <div style={twoColumnFormGridStyle}>
                                <Form.Item
                                    name="rolling_value"
                                    label={labelWithTooltip("监控月数", "MyFlyDocs 自动监控使用按月滚动窗口；例如 2026-05-18 配 1 个月，会扫描 2026-04-18 至 2026-05-18。最多 2 个月。")}
                                    rules={[validateRollingMonths]}
                                    style={compactFormItemStyle}
                                >
                                    <InputNumber min={1} max={MAX_AUTO_RANGE_MONTHS} disabled={!watchedAutoEnabled} style={fullWidthControlStyle} />
                                </Form.Item>
                                <Form.Item name="interval_minutes" label="自动间隔分钟" style={compactFormItemStyle}>
                                    <InputNumber min={5} disabled={!watchedAutoEnabled} style={fullWidthControlStyle} />
                                </Form.Item>
                                <Form.Item
                                    name="force_rescan_recent_days"
                                    label={labelWithTooltip("复扫最近天数", "自动模式下额外回头检查最近 N 天，发现历史日期新增文件后只抓新增文件；0 表示不复扫。")}
                                    style={compactFormItemStyle}
                                >
                                    <InputNumber min={0} max={MAX_AUTO_RANGE_DAYS} disabled={!watchedAutoEnabled} style={fullWidthControlStyle} />
                                </Form.Item>
                            </div>
                        ) : null}
                        {watchedSiteKey === "atlanta" ? (
                            <div style={twoColumnFormGridStyle}>
                                <Form.Item
                                    name="daily_run_time"
                                    label={labelWithTooltip("每日执行时间", "Atlanta 自动模式建议每日 23:59 执行，使用页面 Today 快捷项抓当天 00:00 至 23:59。早晨执行时会改用 Yesterday 快捷项。")}
                                    rules={[
                                        { required: watchedAutoEnabled, message: "请输入每日执行时间" },
                                        { pattern: /^([01][0-9]|2[0-3]):[0-5][0-9]$/, message: "格式应为 HH:mm，例如 23:59" },
                                    ]}
                                    style={compactFormItemStyle}
                                >
                                    <Input placeholder={ATLANTA_DAILY_RUN_TIME} disabled={!watchedAutoEnabled} />
                                </Form.Item>
                                <Form.Item
                                    label="Period 规则"
                                    style={compactFormItemStyle}
                                >
                                    <Input value="Today 00:00 - 23:59" disabled />
                                </Form.Item>
                            </div>
                        ) : null}
                        {!watchedSiteKey ? (
                            <Text type="secondary">填写文件列表 URL 后，会按识别到的站点显示对应的自动抓取规则。</Text>
                        ) : null}
                    </section>

                    <section style={configSectionStyle}>
                        <Collapse
                            ghost
                            style={advancedCollapseStyle}
                            items={[
                                {
                                    key: "advanced",
                                    label: "高级设置",
                                    children: (
                                        <>
                                            <Form.Item name="login_url" label="登录 URL">
                                                <Input placeholder="默认按文件列表 URL 自动识别" />
                                            </Form.Item>
                                            <Form.Item name="remark" label="备注">
                                                <Input.TextArea rows={3} />
                                            </Form.Item>
                                        </>
                                    ),
                                },
                            ]}
                        />
                    </section>
                </Form>
            </Modal>

            <Modal
                title={editingCredential ? "编辑凭据" : "添加登录凭据"}
                open={credentialModalOpen}
                onCancel={() => setCredentialModalOpen(false)}
                onOk={submitCredential}
                destroyOnClose
            >
                <Form form={credentialForm} layout="vertical">
                    <Form.Item name="username" label="账号" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="password"
                        label={editingCredential ? "新密码" : "网站密码"}
                        rules={editingCredential ? [] : [{ required: true, message: "请输入密码" }]}
                        extra={editingCredential ? "留空表示不更换密码；页面不会回显已保存密码。" : "保存后密码只加密存储，不会在页面回显。"}
                    >
                        <Input.Password autoComplete="new-password" />
                    </Form.Item>
                    <Form.Item name="enabled" label="启用凭据" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ExternalCrawlConfigPage;
